/**
 * Product Card Swiper Initialization
 * Initializes Swiper instances for product card images with touch/swipe functionality
 */

class ProductCardSwiper {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // Initialize existing swipers
    this.initSwipers();
    
    // Watch for new product cards being added (for dynamic content)
    this.observeNewCards();
  }

  initSwipers() {
    const swiperContainers = document.querySelectorAll('.rm-product-card-swiper');
    
    swiperContainers.forEach((container, index) => {
      const productId = container.dataset.productId;
      
      // Skip if already initialized
      if (this.swipers.has(productId)) {
        return;
      }

      // Check if there are multiple slides
      const slides = container.querySelectorAll('.swiper-slide');
      
      if (slides.length <= 1) {
        return;
      }

      // Wait for images to load before initializing swiper
      const images = container.querySelectorAll('.swiper-slide img');
      let loadedImages = 0;
      
      if (images.length > 0) {
        images.forEach(img => {
          if (img.complete) {
            loadedImages++;
          } else {
            img.addEventListener('load', () => {
              loadedImages++;
              if (loadedImages === images.length) {
                this.initializeSwiper(container, productId, slides);
              }
            });
            img.addEventListener('error', () => {
              loadedImages++;
              if (loadedImages === images.length) {
                this.initializeSwiper(container, productId, slides);
              }
            });
          }
        });
        
        // If all images are already loaded, initialize immediately
        if (loadedImages === images.length) {
          this.initializeSwiper(container, productId, slides);
        }
      } else {
        // No images found, initialize anyway
        this.initializeSwiper(container, productId, slides);
      }
    });
  }

    initializeSwiper(container, productId, slides) {
    // Check if we're on desktop
    const isDesktop = window.innerWidth > 768;



    // Check if pagination element exists
    const paginationElement = container.parentElement.querySelector('.rm-product-card-pagination-track .swiper-pagination');
    if (!paginationElement) {
      console.error('❌ Pagination element not found for product:', productId);
      return;
    }

    // Initialize Swiper with simplified configuration
    const swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        allowTouchMove: !isDesktop, // Disable touch on desktop
        grabCursor: !isDesktop, // Disable grab cursor on desktop
        autoplay: false,
        // Touch settings - improved for better vertical scrolling
        touchRatio: 1,
        touchAngle: 30, // Reduced angle to be more selective about horizontal swipes
        simulateTouch: !isDesktop, // Enable simulated touch on mobile
        shortSwipes: !isDesktop,
        longSwipes: !isDesktop,
        longSwipesRatio: 0.5,
        longSwipesMs: 300,
        followFinger: !isDesktop,
        threshold: 10, // Increased threshold to be more selective
        touchMoveStopPropagation: false, // Allow event propagation for better Safari compatibility
        iOSEdgeSwipeDetection: true, // Always enable iOS edge detection
        iOSEdgeSwipeThreshold: 20,
        touchEventsTarget: 'container', // Target container instead of wrapper for better Safari compatibility
        // Improved touch handling for mobile
        touchStartPreventDefault: false, // Allow default touch behavior
        touchStartForcePreventDefault: false, // Don't force prevent default
        touchReleaseOnEdges: true, // Allow release on edges for better scrolling
        // Pagination - enabled for mobile only, target the new pagination track
        pagination: {
          el: paginationElement,
          clickable: true,
          dynamicBullets: false,
          type: 'bullets'
        },
      on: {
        init: function() {
          container.classList.add('swiper-initialized');
          if (isDesktop) {
            container.classList.add('swiper-desktop-disabled');
          }
          
          // Improved touch handling for all mobile browsers
          if (!isDesktop) {
            // Allow both horizontal and vertical touch actions
            container.style.touchAction = 'manipulation';
            
            // Add smart touch detection
            let touchStartX = 0;
            let touchStartY = 0;
            let isHorizontalSwipe = false;
            
            container.addEventListener('touchstart', function(e) {
              if (e.touches.length === 1) {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                isHorizontalSwipe = false;
              }
            }, { passive: true });
            
            container.addEventListener('touchmove', function(e) {
              if (e.touches.length === 1 && !isHorizontalSwipe) {
                const touchCurrentX = e.touches[0].clientX;
                const touchCurrentY = e.touches[0].clientY;
                const deltaX = Math.abs(touchCurrentX - touchStartX);
                const deltaY = Math.abs(touchCurrentY - touchStartY);
                
                // If horizontal movement is greater than vertical, it's a horizontal swipe
                if (deltaX > deltaY && deltaX > 15) {
                  isHorizontalSwipe = true;
                  // Allow swiper to handle this touch
                  e.stopPropagation();
                } else if (deltaY > deltaX && deltaY > 15) {
                  // Vertical swipe - let it pass through for scrolling
                  isHorizontalSwipe = false;
                }
              }
            }, { passive: true });
            
            // Ensure wrapper has correct touch handling
            const wrapper = container.querySelector('.swiper-wrapper');
            if (wrapper) {
              wrapper.style.touchAction = 'manipulation';
            }
          }
       
          
          // Debug image loading
          const slides = container.querySelectorAll('.swiper-slide img');
          slides.forEach((img, index) => {
            
            // Check if image is actually visible
            const rect = img.getBoundingClientRect();
            
            // Check if image has loaded
            if (!img.complete) {
              img.addEventListener('error', (e) => {
                console.error(`Slide ${index + 1} image failed to load:`, e);
              });
            }
            
            // Force image to be visible
            img.style.opacity = '1';
            img.style.visibility = 'visible';
            img.style.display = 'block';
          });
          
          // Force all slides to be visible
          const allSlides = container.querySelectorAll('.swiper-slide');
          allSlides.forEach((slide, index) => {
            slide.style.opacity = '1';
            slide.style.visibility = 'visible';
            slide.style.display = 'block';
          });
          
          this.update();
          
          // Initialize pagination with first slide active
          if (this.pagination && this.pagination.bullets) {
            const bullets = this.pagination.bullets;
            bullets.forEach((bullet, index) => {
              if (index === 0) {
                bullet.classList.add('swiper-pagination-bullet-active');
              } else {
                bullet.classList.remove('swiper-pagination-bullet-active');
              }
            });
          } 
          
          // Add click handler for navigation (but not on pagination)
          const productUrl = container.dataset.productUrl;
          if (productUrl) {
            container.addEventListener('click', (e) => {
              // Don't navigate if clicking on pagination
              if (e.target.closest('.swiper-pagination')) {
                return;
              }
              // Navigate to product page
              window.location.href = productUrl;
            });
          }
          
          // Ensure pagination clicks work properly
          const paginationEl = container.querySelector('.swiper-pagination');
          if (paginationEl) {
            paginationEl.addEventListener('click', (e) => {
              e.stopPropagation(); // Prevent navigation
            });
          }
          
          // Demo swipe animation for first swiper only (mobile only)
          const isFirstSwiperOnPage = container === document.querySelector('.rm-product-card-swiper');
          if (isFirstSwiperOnPage && !isDesktop && slides.length > 1) {
            
            setTimeout(() => {
              // Get the wrapper element
              const wrapper = container.querySelector('.swiper-wrapper');
              if (wrapper) {
                // Calculate 30% of slide width for partial swipe
                const slideWidth = wrapper.children[0].offsetWidth;
                const swipeDistance = slideWidth * 0.4; // 40% of slide width
                
                // Animate to 30% position
                wrapper.style.transition = 'transform 400ms ease-out';
                wrapper.style.transform = `translateX(-${swipeDistance}px)`;
                
                // Animate back to original position
                setTimeout(() => {
                  wrapper.style.transform = 'translateX(0px)';
                  // Clean up transition after animation
                  setTimeout(() => {
                    wrapper.style.transition = '';
                  }, 400);
                }, 1200);
              }
            }, 1000); // Wait 1 second after page load
          }
          
          // Touch functionality is now handled by the smart touch detection above
        },
        touchStart: function(swiper, event) {
        },
        touchMove: function(swiper, event) {
        },
        touchEnd: function(swiper, event) {
          // Pagination is already handled by slideChange event
        },
        slideChange: function() {

          // Force Swiper to update its pagination to match the current slide
          for (const child of this.pagination.el.children) {
            child.classList.remove('swiper-pagination-bullet-active');
          }
          this.pagination.el.children[this.activeIndex].classList.add('swiper-pagination-bullet-active');

        },
        
        // Add more event listeners for debugging
        init: function() {
        },
        
        beforeInit: function() {
        },
        
        afterInit: function() {
        }
      }
    });

    // Store swiper instance
    this.swipers.set(productId, swiper);
  }

  observeNewCards() {
    // Use MutationObserver to watch for new product cards
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains('rm-product-card')) {
                shouldReinit = true;
              } else if (node.querySelector && node.querySelector('.rm-product-card-swiper')) {
                shouldReinit = true;
              }
            }
          });
        }
      });

      if (shouldReinit) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.initSwipers();
        }, 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Method to destroy all swipers (useful for cleanup)
  destroyAll() {
    this.swipers.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers.clear();
  }

  // Method to reinitialize all swipers
  reinit() {
    this.destroyAll();
    this.initSwipers();
  }

  // Debug method to check current swiper state
  debugSwiperState(productId) {
    const swiper = this.swipers.get(productId);
    if (swiper) {
      
      // Check pagination bullet classes
      if (swiper.pagination.bullets) {
        swiper.pagination.bullets.forEach((bullet, index) => {
          const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
        });
      }
    }
  }

  // Method to ensure all slides are visible
  ensureAllSlidesVisible(container) {
    const slides = container.querySelectorAll('.swiper-slide');
    const images = container.querySelectorAll('.swiper-slide img');
    
    
    slides.forEach((slide, index) => {
      // Force slide to be visible
      slide.style.opacity = '1';
      slide.style.visibility = 'visible';
      slide.style.display = 'block';
      slide.style.position = 'absolute';
      slide.style.top = '0';
      slide.style.left = '0';
      slide.style.width = '100%';
      slide.style.height = '100%';
      
    });
    
    images.forEach((img, index) => {
      // Force image to be visible
      img.style.opacity = '1';
      img.style.visibility = 'visible';
      img.style.display = 'block';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.objectPosition = 'center';
      
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if Swiper is available
  if (typeof Swiper !== 'undefined') {
    window.productCardSwiper = new ProductCardSwiper();
  } else {
      console.warn('Swiper not found. Product card swiper functionality disabled.');
  }
});

// Initialize on page load (for cases where DOMContentLoaded already fired)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof Swiper !== 'undefined') {
      window.productCardSwiper = new ProductCardSwiper();
    }
  });
} else {
  if (typeof Swiper !== 'undefined') {
    window.productCardSwiper = new ProductCardSwiper();
  }
} 